import { transactionsUtils } from 'app-components/flux/transactions';
import type { CashFlowTransaction } from 'app-components/flux/transactions/transactionsTypes';
import {
  isInvoiceTxn,
  isManualTransaction,
  isRefundTxn,
  isUITransferTxn,
} from 'app-components/flux/transactions/transactionsUtils';
import type { TransactionDetailsProjectSelect } from 'app-components/flux/transactions/transactionDetailsForm';
import type { ScheduledTransaction } from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';

type Params = {
  accountId: string;
  attachments?: Array<{ id?: string | null }> | null;
  customersAndProjects: TransactionDetailsProjectSelect;
  dateIsValid: boolean;
  dateError?: string;
  disableSalesReceiptFields?: boolean;
  isSplitSeriesEnabled?: boolean;
  ornamentType: string;
  openAccounts: { get: (accountId: string) => { isClosed?: boolean } | undefined } | undefined;
  payee: string;
  seriesTxn: ScheduledTransaction['transaction'] | null;
  split: CashFlowTransaction['split'];
  txn: CashFlowTransaction;
};

export function useTransactionDetailsDisplayState({
  accountId,
  attachments,
  customersAndProjects,
  dateError,
  dateIsValid,
  disableSalesReceiptFields,
  isSplitSeriesEnabled,
  openAccounts,
  ornamentType,
  payee,
  seriesTxn,
  split,
  txn,
}: Params): {
  clientSelectedPayment: boolean;
  completeEnough: boolean;
  hasSplitSeries: boolean;
  hideCreateRule?: boolean;
  hideClientsAndProjects: boolean;
  isAccountClosed: boolean | undefined;
  validAttachments: boolean;
  validPayee: boolean;
} {
  const hideCreateRule =
    isInvoiceTxn(txn) ||
    isRefundTxn(txn) ||
    isUITransferTxn(txn) ||
    isManualTransaction(txn) ||
    disableSalesReceiptFields;
  const validAttachments = !attachments || !attachments.find((item) => item && !item.id);
  const splitItems = (split ?? txn.split)?.items;
  const hasSplitItems = Array.isArray(splitItems) ? splitItems.length > 0 : Number(splitItems?.size ?? 0) > 0;
  const clientSelectedPayment = hasSplitItems
    ? true
    : customersAndProjects.billable || (ornamentType === 'payment' && customersAndProjects.payment)
      ? Boolean(customersAndProjects.customerId)
      : true;
  const validPayee = Boolean(payee && payee !== '');
  const completeEnough = Boolean(
    validPayee && accountId && dateIsValid && validAttachments && !dateError && clientSelectedPayment,
  );
  const isAccountClosed = accountId ? openAccounts?.get(accountId)?.isClosed : undefined;
  const hasSplitSeries = Boolean(
    isSplitSeriesEnabled &&
      txn.stModelId &&
      seriesTxn &&
      transactionsUtils.isSplitTxn(seriesTxn) &&
      seriesTxn.split?.items,
  );
  const hideClientsAndProjects = hasSplitItems;

  return {
    clientSelectedPayment,
    completeEnough,
    hasSplitSeries,
    hideCreateRule,
    hideClientsAndProjects,
    isAccountClosed,
    validAttachments,
    validPayee,
  };
}
